import {pillowLayout} from './pillow-layout'
import { facetedStairProfile } from './faceted-stair-profile'
import { hasOpenRiserSides } from '../../../../../config/blissStairSides'
import { isXGuardrail } from '../../../../../config/blissGuardrails'
import {createBeddingSet} from './bedding-model'
import type {BeddingSelection} from '../../../../../config/blissBedding'
import { createFacePanel, rectangularFace } from './face-panel-model'
import type { SurfaceFaceStyle } from '../../../../../config/blissRoomFinish'
import { mattressThickness, type MattressThickness } from '../../../../../config/blissRoomFinish'
import { createDrawerFace } from './drawer-face'
import { customTrimOmissions } from './custom-detail-takeoff'
import { addBedTrim } from './bed-trim-model'
import { addLowerShiplap } from './lower-shiplap-model'
import { addCabinBookshelf } from './cabin-bookshelf-model'
import { addCabinCrown } from './crown-molding'
import type { CrownMoldingStyle } from '../../../../../config/blissRoomFinish'
import type { DrawerPullStyle, BedTrimStyle, DrawerFaceStyle } from '../../../../../config/blissRoomFinish'
import * as T from 'three'
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js'
import { createPillow, fabricTexture } from './soft-bedding'
import { bounds, type Bed } from './beds'
import type { Opening, Room } from './model'
import { landingRearNeedsGuard } from './landing-guard'
import type { FinishPart } from './finish-materials'
import { BUNK_BOARD_THICKNESS, BUNK_CORNER_FACE } from './bunk-joinery'
import { createXGuardrailPanel } from './x-guardrail-model'
import { X_GUARDRAIL, type BasicGuardrailStyle } from '../../../../../config/blissGuardrails'
import { CUSTOM, customUpperOffsets, customFrameDepth, customStairProfile, hasStairDimensions, customVerticalProfile, customGeometry, customStringerSupportPosts, hasCompactStringerStairs, hasCompactStairs, customStyle, type BedRect } from './custom-layout'
import { stringerGeometry, stringerOutline } from './stringer-geometry'


/** Dimensioned planning geometry. Wall anchorage is intended, not an engineered connection. */
export function createBridgeModel(
  bed: Bed,
  room: Room,
  guardBars: 2 | 3 = 2,
  guardrailStyle?: BasicGuardrailStyle,
  openings: Opening[] = [],
  drawerFaceStyle: DrawerFaceStyle = 'shaker',
  drawerPullStyle?: DrawerPullStyle,
  bedTrim?: BedTrimStyle,
  mattresses?: MattressThickness,
  crownMolding?: CrownMoldingStyle,
  stringerFaceStyle: SurfaceFaceStyle = 'nickel_gap',
  upperBunkFaceStyle: SurfaceFaceStyle = 'flat',
  beddingSelection?: BeddingSelection
) {
  if (guardrailStyle === 'horizontal_3bar') guardBars = 3
  const root = new T.Group(),
    g = customGeometry(bed),
    style = customStyle(bed),
    profile = customVerticalProfile(bed),
    deck = profile.deckHeight,
    frameDepth = customFrameDepth(bed),
    { frameRise, mattressLift } = customUpperOffsets(bed)
  // The cabin enclosure meets the measured room ceiling. Never use the generic
  // bunk/guard profile height as its roof height: that lowers the roof to 90".
  // The divider shares this same envelope and stays above the lower-bed zone.
  const cabinTop = room.height
  const cabinBottom = deck - frameDepth
  const cabinHeight = cabinTop - cabinBottom
  const material = (part: FinishPart | null) =>
    new T.MeshStandardMaterial({ color: part ? 0xe2cea8 : 0xfaf6f0, roughness: 0.85 })
  const local = (x: number, y: number, z: number) =>
    new T.Vector3((x - bed.width / 2) / 12, y / 12, (z - bed.depth / 2) / 12)
  function add(name: string, geometry: T.BufferGeometry, part: FinishPart | null = 'frame') {
    const mesh = new T.Mesh(geometry, material(part))
    mesh.name = name
    if (part) mesh.userData.finishPart = part
    if (part === 'topRail' && !isXGuardrail(guardrailStyle)) mesh.userData.railLevel = guardBars
    if (!name.endsWith('-mattress'))
      mesh.add(
        new T.LineSegments(
          new T.EdgesGeometry(geometry),
          new T.LineBasicMaterial({ color: 0x9a8268, transparent: true, opacity: 0.4 })
        )
      )
    root.add(mesh)
    return mesh
  }
  function box(
    name: string,
    x: number,
    y: number,
    z: number,
    w: number,
    h: number,
    d: number,
    part: FinishPart | null = 'frame'
  ) {
    const geometry = name.endsWith('-mattress')
      ? new RoundedBoxGeometry(w / 12, h / 12, d / 12, 3, 0.09)
      : new T.BoxGeometry(w / 12, h / 12, d / 12)
    const mesh = add(name, geometry, part)
    if (name.endsWith('-mattress')) {
      ;(mesh.material as T.MeshStandardMaterial).map = fabricTexture()
    }
    mesh.position.copy(local(x + w / 2, y + h / 2, z + d / 2))
    mesh.userData.boxInches = { x, y, z, w, h, d }
    return mesh
  }
  function bar(
    name: string,
    a: T.Vector3,
    b: T.Vector3,
    thickness = 1.5,
    part: FinishPart = 'accent'
  ) {
    const vector = b.clone().sub(a),
      mesh = add(
        name,
        new T.BoxGeometry(thickness / 12, vector.length() / 12, thickness / 12),
        part
      )
    mesh.position.copy(local((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2))
    mesh.quaternion.setFromUnitVectors(new T.Vector3(0, 1, 0), vector.normalize())
    return mesh
  }
  const guardPosts = new Set<string>()
  const pattern =
    guardrailStyle === 'horizontal' || guardrailStyle === 'horizontal_3bar'
      ? 'horizontal'
      : isXGuardrail(guardrailStyle)
        ? 'cross'
        : style === 'rosemary' || style === 'watercolor'
          ? 'cross'
          : style === 'scenic'
            ? 'vertical'
            : 'horizontal'
  function guard(name: string, x1: number, z1: number, x2: number, z2: number, design = pattern, guardDeck = deck) {
    const a = new T.Vector3(x1, 0, z1),
      b = new T.Vector3(x2, 0, z2),
      len = a.distanceTo(b)
    // Native X panels bear on the deck. Their uprights belong to the panel itself,
    // with no exposed post extension lifting the bottom rail above the frame.
    const crossBase = guardDeck,
      crossTop = crossBase + (isXGuardrail(guardrailStyle) ? X_GUARDRAIL.height : 21)
    // Horizontal/vertical landing guards retain their existing supporting uprights.
    for (const p of [a, b]) {
      const key = `${p.x}:${p.z}`
      if (!guardPosts.has(key))
        box(
          `${name}-post`,
          p.x - 1.5,
          guardDeck,
          p.z - 1.5,
          3,
          (design === 'cross' ? crossTop : guardDeck + 30) - guardDeck,
          3
        )
      guardPosts.add(key)
    }
    if (design === 'horizontal') {
      const face = guardBars === 3 ? 2.5 : 3.5
      for (let i = 0; i < guardBars; i++) {
        const y = guardDeck + CUSTOM.mattress + 3.5 + i * (face + 3.5)
        if (z1 === z2)
          box(`${name}-rail`, Math.min(x1, x2), y, z1 - 0.375, len, face, 0.75, i === guardBars - 1 ? 'topRail' : i === 0 ? 'rail1' : 'rail2')
        else box(`${name}-rail`, x1 - 0.375, y, Math.min(z1, z2), 0.75, face, len, i === guardBars - 1 ? 'topRail' : i === 0 ? 'rail1' : 'rail2')
      }
    } else
      for (const y of design === 'cross' ? [crossBase + 1.5, crossTop - 1.5] : [70.5, 88.5])
        bar(`${name}-rail`, a.clone().setY(y), b.clone().setY(y), 3, y === (design === 'cross' ? crossTop - 1.5 : 88.5) ? 'topRail' : 'accent')
    const panels = Math.max(1, Math.ceil(len / 38))
    if (design === 'cross')
      for (let i = 0; i < panels; i++) {
        const start = a.clone().lerp(b, i / panels),
          end = a.clone().lerp(b, (i + 1) / panels)
        bar(`${name}-cross`, start.clone().setY(crossBase + 3), end.clone().setY(crossTop - 3), 2)
        bar(`${name}-cross`, start.clone().setY(crossTop - 3), end.clone().setY(crossBase + 3), 2)
        if (i > 0)
          box(
            `${name}-divider`,
            start.x - 0.75,
            crossBase + 3,
            start.z - 0.75,
            1.5,
            crossTop - crossBase - 6,
            1.5,
            'accent'
          )
      }
    if (design === 'vertical')
      for (let d = 5; d < len - 3; d += 6) {
        const p = a.clone().lerp(b, d / len)
        box(`${name}-slat`, p.x - 0.75, 72, p.z - 0.75, 1.5, 15, 1.5, 'accent')
      }
  }
  // Same outside L-corner and staggered inner rail seam as the Basic models.
  // u spans mattress width; v runs from the pillow end to the entry end.
  function woodGuards(r: BedRect, i: number) {
    const t = BUNK_BOARD_THICKNESS,
      width = r.axis === 'x' ? r.h : r.w,
      length = r.axis === 'x' ? r.w : r.h,
      dividerCabin = style === 'divider',
      // Divider cabins climb at the two extreme outside ends. Cut the guard only
      // at that ladder; do not leave the old central/landing opening.
      entry = dividerCabin ? 0 : r.axis === 'x' ? 3 : 10,
      entryEnd = dividerCabin ? 18 : r.axis === 'x' && g.landing ? Math.min(entry + 24, g.landing.h) : entry + 24,
      face = guardBars === 3 ? 2.5 : 3.5,
      base = (pattern === 'cross' ? deck : deck + CUSTOM.mattress + 3.5) + frameRise,
      top = base + guardBars * face + (guardBars - 1) * 3.5,
      postTop = pattern === 'cross' ? base : top,
      postBase = style === 'inlet' ? 0 : deck
    function board(
      name: string,
      u: number,
      y: number,
      v: number,
      w: number,
      h: number,
      d: number,
      part: FinishPart = 'frame'
    ) {
      if (w <= 0 || h <= 0 || d <= 0) return
      if (r.axis === 'z') return box(`upper-${i}-${name}`, r.x + u, y, r.y + v, w, h, d, part)
      return box(
        `upper-${i}-${name}`,
        r.head === 'left' ? r.x + v : r.x + r.w - v - d,
        y,
        r.y + u,
        d,
        h,
        w,
        part
      )
    }
    if (frameRise > 0) {
      const boardStock = 11 / 16
      board('raised-long-board-left', 0, deck, 0, boardStock, frameRise, length)
      board('raised-long-board-right', width-boardStock, deck, 0, boardStock, frameRise, length)
      board('raised-head-board', boardStock, deck, 0, width-2*boardStock, frameRise, boardStock)
      board('raised-foot-board-left', boardStock, deck, length-boardStock, entry-boardStock, frameRise, boardStock)
      board('raised-foot-board-right', entryEnd, deck, length-boardStock, width-boardStock-entryEnd, frameRise, boardStock)
    }
    for (const right of [false, true])
      for (const foot of [false, true]) {
        // Keep the entry aligned with the supported landing surface.
        const f = foot && !right ? Math.min(BUNK_CORNER_FACE, entry) : BUNK_CORNER_FACE,
          u = right ? width : 0,
          v = foot ? length : 0,
          label = `corner-${right ? 'right' : 'left'}-${foot ? 'foot' : 'head'}`
        board(
          `${label}-end`,
          right ? u - f : u,
          postBase,
          foot ? v - t : v,
          f,
          postTop - postBase,
          t
        )
        board(
          `${label}-side`,
          right ? u - t : u,
          postBase,
          foot ? v - f : v + t,
          t,
          postTop - postBase,
          f - t
        )
      }
    if (pattern === 'horizontal') {
      for (let n = 0; n < guardBars; n++) {
        const y = base + n * (face + 3.5)
        // Side boards run past the ends of the cross boards, opposite the outer L seam.
        for (const u of [t, width - 2 * t]) {
          if (dividerCabin && ((i === 0 && u === t) || (i === 1 && u !== t))) {
            // Outside ladder side: begin the rail after the 18-inch entry.
            board('long-guard-rail', u, y, entryEnd, t, face, length - entryEnd - t, n === guardBars - 1 ? 'topRail' : n === 0 ? 'rail1' : 'rail2')
          } else board('long-guard-rail', u, y, t, t, face, length - 2 * t, n === guardBars - 1 ? 'topRail' : n === 0 ? 'rail1' : 'rail2')
        }
        board('head-guard-rail', 2 * t, y, t, width - 4 * t, face, t, n === guardBars - 1 ? 'topRail' : n === 0 ? 'rail1' : 'rail2')
        board('foot-return-rail', 2 * t, y, length - 2 * t, entry - 2 * t, face, t, n === guardBars - 1 ? 'topRail' : n === 0 ? 'rail1' : 'rail2')
        board(
          'foot-return-rail',
          entryEnd,
          y,
          length - 2 * t,
          width - 2 * t - entryEnd,
          face,
          t,
          n === guardBars - 1 ? 'topRail' : n === 0 ? 'rail1' : 'rail2'
        )
      }
      // Back each cut rail end without narrowing the opening.
      if (entry > BUNK_CORNER_FACE)
        board(
          'entry-left-post',
          entry - BUNK_CORNER_FACE,
          deck,
          length - t,
          BUNK_CORNER_FACE,
          top - deck,
          t
        )
      if (width - entryEnd > BUNK_CORNER_FACE)
        board('entry-right-post', entryEnd, deck, length - t, BUNK_CORNER_FACE, top - deck, t)
    } else {
      const assembly = new T.Group(),
        stock = X_GUARDRAIL.stock
      assembly.name = `upper-${i}-x-pattern-guardrails`
      function panel(name: string, span: number, u: number, v: number, angle = 0) {
        if (span <= 0) return
        const p = createXGuardrailPanel(span, `upper-${i}-guard-x-${name}`)
        p.position.set(u / 12, base / 12, v / 12)
        p.rotation.y = angle
        if (frameRise >= 2) {
          // Backing alongside the long board, with its bearing surface flush at rail base.
          const filler = new T.Mesh(new T.BoxGeometry(span/12,2/12,.75/12),material('frame'))
          filler.name = `upper-${i}-guard-filler-${name}`
          filler.userData.finishPart = 'frame'
          filler.position.set(span/24,-1/12,(11/16+.75/2)/12)
          p.add(filler)
        }
        assembly.add(p)
      }
      if (guardrailStyle !== 'x_pattern_front') panel('left', length, stock, 0, -Math.PI / 2)
      panel('right', length, width, 0, -Math.PI / 2)
      if (guardrailStyle !== 'x_pattern_front' && style !== 'rosemary') panel('head', width - 2 * stock, stock, 0)
      if (guardrailStyle !== 'x_pattern_front') panel('foot-left', entry - stock, stock, length - stock)
      panel('foot-right', width - stock - entryEnd, entryEnd, length - stock)
      if (r.axis === 'z') assembly.position.copy(local(r.x, 0, r.y))
      else {
        assembly.position.copy(local(r.head === 'left' ? r.x : r.x + r.w, 0, r.y))
        assembly.rotation.y = r.head === 'left' ? Math.PI / 2 : -Math.PI / 2
        if (r.head === 'left') assembly.scale.x = -1
      }
      root.add(assembly)
    }
  }
  function pillow(name: string, x: number, y: number, z: number, w: number, h: number, d: number) {
    const mesh = createPillow(w, d, h)
    mesh.name = name
    mesh.position.copy(local(x + w / 2, y + h / 2, z + d / 2))
    mesh.userData.boxInches = { x, y, z, w, h, d }
    root.add(mesh)
  }
  function bedding(r: BedRect, y: number, name: string) {
    const thickness = mattressThickness(mattresses,name.startsWith("upper")?"upper":"lower")
    const alongX = r.axis === 'x',
      wide = r.size.startsWith('Twin') ? 38 : r.size === 'Queen' ? 60 : 54,
      length = r.size === 'Twin XL' || r.size === 'Queen' ? 80 : 75,
      mw = alongX ? length : wide,
      md = alongX ? wide : length
    if (name.startsWith('upper') && mattressLift > 0)
      box(`${name}-mattress-support`,r.x+(r.w-mw)/2,y-mattressLift,r.y+(r.h-md)/2,mw,mattressLift,md)
    box(`${name}-mattress`, r.x + (r.w - mw) / 2, y, r.y + (r.h - md) / 2, mw, thickness, md, null)
    const upper=name.startsWith('upper'),beddingId=beddingSelection?.[upper?'upper':'lower']??'original'
    if(beddingId!=='original'){
      const set=createBeddingSet(wide,length,beddingId,upper)
      set.name=`${name}-bedding-${beddingId}`
      if(upper && style==='rosemary'){
        // Bedding starts at the inset mattress edge; the headboard sits at the frame edge.
        const mattressInset=((alongX?r.w:r.h)-length)/2
        const headboardOffset=BUNK_BOARD_THICKNESS-mattressInset-.5
        set.children.filter(child=>child.userData.pillowKey).forEach(pillow=>{pillow.position.z+=headboardOffset/12})
      }
      if(alongX){set.position.copy(local(r.head==='right'?r.x+(r.w+length)/2:r.x+(r.w-length)/2,y+thickness,r.y+r.h/2));set.rotation.y=r.head==='right'?-Math.PI/2:Math.PI/2}
      else set.position.copy(local(r.x+r.w/2,y+thickness,r.y+(r.h-length)/2))
      root.add(set);return
    }
    const layout=pillowLayout(alongX?r.h:r.w)
    for(const offset of layout.offsets){
      if(alongX){
        const px=r.head==='right'?r.x+r.w-layout.depth-3:r.x+3
        pillow(`${name}-pillow`,px,y+thickness,r.y+r.h/2+offset-layout.width/2,layout.depth,5,layout.width)
      }else pillow(`${name}-pillow`,r.x+r.w/2+offset-layout.width/2,y+thickness,r.y+3,layout.width,5,layout.depth)
    }
  }
  if (style === 'divider') {
    // Luxury floating-cabin treatment: each upper berth reads as a complete architectural
    // compartment, not an exposed platform. Back/ceiling/outer-end panels frame the mattress;
    // the room-facing side stays open except for its required guard/entry treatment.
    g.uppers.forEach((r, i) => {
      const shell = 1.5
      // Back, both ends and roof remain a complete ceiling-height enclosure.
      box(`upper-${i}-cabin-back`, r.x, cabinBottom, r.y, r.w, cabinHeight, shell)
      box(`upper-${i}-cabin-ceiling`, r.x, cabinTop - shell, r.y, r.w, shell, r.h)
      box(`upper-${i}-cabin-left-wall`, r.x, cabinBottom, r.y, shell, cabinHeight, r.h)
      box(`upper-${i}-cabin-right-wall`, r.x + r.w - shell, cabinBottom, r.y, shell, cabinHeight, r.h)
    })
  }
  if (style === 'divider' && g.divider && g.divider.w > 0) {
    const d = g.divider
    // Width changes only its horizontal span. Its top is exactly the roof top;
    // its bottom is exactly the fascia underside, never the floor. The two cabin
    // end panels close a zero-width gap, so do not emit a degenerate divider mesh.
    // Ceiling/truss load-bearing connections still require structural verification.
    box('adjustable-center-divider', d.x, cabinBottom, d.y, d.w, cabinHeight, d.h)
  }
  g.uppers.forEach((r, i) => {
    // A substantial boxed fascia, not a thin mattress shelf. Actual Twin size stays 38 × 75.
    const inset = style === 'inlet' ? BUNK_BOARD_THICKNESS : 0
    box(
      `upper-${i}-deck`,
      r.x + inset,
      deck - frameDepth,
      r.y + inset,
      r.w - 2 * inset,
      frameDepth,
      r.h - 2 * inset
    )
    if (!hasCompactStairs(bed)) {
    if (style === 'scenic')
      box(`upper-${i}-wall-ledger`, r.x, deck - frameDepth - (hasCompactStringerStairs(bed) ? 0 : 4), r.y, 3, 4, r.h)
    else
      box(
        `upper-${i}-wall-ledger`,
        r.x + inset,
        deck - frameDepth - (hasCompactStringerStairs(bed) ? 0 : 4),
        r.y + inset,
        r.w - 2 * inset,
        4,
        3
      )
    }
    if (upperBunkFaceStyle !== 'flat') {
      for (const [edge, x, z, width, angle] of [
        ['front',r.x,r.y+r.h,r.w,0], ['back',r.x+r.w,r.y,r.w,Math.PI],
        ['left',r.x,r.y,r.h,-Math.PI/2], ['right',r.x+r.w,r.y+r.h,r.h,Math.PI/2],
      ] as const) {
        const height = frameDepth + frameRise
        const foot = r.axis === 'x' ? (r.head === 'left' ? 'right' : 'left') : 'front'
        const entry = r.axis === 'x' ? 3 : 10
        const entryEnd = r.axis === 'x' && g.landing ? Math.min(entry+24,g.landing.h) : entry+24
        const a = edge === 'right' ? width-entryEnd : entry
        const b = edge === 'right' ? width-entry : entryEnd
        const outline = edge === foot && frameRise > 0
          ? [[0,0],[width,0],[width,height],[b,height],[b,frameDepth],[a,frameDepth],[a,height],[0,height]]
          : [[0,0],[width,0],[width,height],[0,height]]
        const face=frameRise > 0 ? createFacePanel(outline.map(([x,y])=>new T.Vector2(x,y)),upperBunkFaceStyle) : rectangularFace(width,frameDepth,upperBunkFaceStyle)
        face.name=`upper-${i}-${edge}-face-${upperBunkFaceStyle}`
        face.rotation.y=angle
        face.position.copy(local(x,deck-frameDepth,z))
        root.add(face)
      }
    }
    addBedTrim(root,r,deck+frameRise,false,bedTrim,bed,customTrimOmissions(bed,r,true))
    bedding(r, deck+mattressLift, `upper-${i}`)
    if (style === 'rosemary') {
      // The rail owns the corner; the headboard butts into its inside face.
      // A tiny reveal prevents coplanar end faces from flickering through one another.
      const thickness = BUNK_BOARD_THICKNESS
      const railStock = isXGuardrail(guardrailStyle) ? X_GUARDRAIL.stock : pattern === 'cross' ? 3 : BUNK_BOARD_THICKNESS
      const backInset = guardrailStyle === 'x_pattern_front' ? 0 : railStock
      const frontInset = railStock + 0.02
      if (r.axis === 'x')
        box(`upper-${i}-outer-headboard`, r.head === 'right' ? r.x + r.w - thickness : r.x,
          deck, r.y + backInset, thickness, 14, r.h - backInset - frontInset)
      else box(`upper-${i}-outer-headboard`, r.x + backInset, deck, r.y, r.w - backInset - frontInset, 14, thickness)
    }
    if (style === 'divider') {
      // Cabin front guard: mirrored 18-inch entry at the extreme OUTSIDE end.
      // Left cabin opens on its left end; right cabin opens on its right end.
      const face = guardBars === 3 ? 2.5 : 3.5
      const base = deck + CUSTOM.mattress + 3.5
      const opening = Math.max(12, Math.min(18, bed.ladderWidth ?? 18))
      const top = base + guardBars * face + (guardBars - 1) * 3.5
      for (let n = 0; n < guardBars; n++) {
        const y = base + n * (face + 3.5)
        const start = i === 0 ? r.x + opening : r.x
        const end = i === 0 ? r.x + r.w : r.x + r.w - opening
        box(`upper-${i}-cabin-front-guard-${n}`, start, y, r.y + r.h - BUNK_BOARD_THICKNESS, end - start, face, BUNK_BOARD_THICKNESS, n === guardBars - 1 ? 'topRail' : n === 0 ? 'rail1' : 'rail2')
      }
      // This is a guard post, NOT a cabin wall. Seat it on the deck and cap it
      // flush with the actual top rail. Roof/ceiling height must not affect it.
      const openingPostX = i === 0 ? r.x + opening : r.x + r.w - opening - BUNK_CORNER_FACE
      box(`upper-${i}-cabin-entry-post`, openingPostX, deck, r.y + r.h - BUNK_BOARD_THICKNESS, BUNK_CORNER_FACE, top - deck, BUNK_BOARD_THICKNESS)
    } else if (pattern === 'horizontal' || isXGuardrail(guardrailStyle)) woodGuards(r, i)
    else if (r.axis === 'x') {
      for (const z of [r.y + 1.5, r.y + r.h - 1.5])
        guard(`upper-${i}-long-guard`, r.x + 1.5, z, r.x + r.w - 1.5, z)
      const head = r.head === 'left' ? r.x + 1.5 : r.x + r.w - 1.5,
        foot = r.head === 'left' ? r.x + r.w - 1.5 : r.x + 1.5
      if (style !== 'rosemary') guard(`upper-${i}-head-guard`, head, r.y + 1.5, head, r.y + r.h - 1.5)
      // End the opening at the landing edge, so the return also guards the set-back flight.
      const entryEnd = Math.min(27, g.landing?.h ?? 27)
      guard(`upper-${i}-foot-return`, foot, r.y + entryEnd + 1.5, foot, r.y + r.h - 1.5)
    } else {
      for (const x of [r.x + 1.5, r.x + r.w - 1.5])
        guard(`upper-${i}-long-guard`, x, r.y + 1.5, x, r.y + r.h - 1.5)
      guard(`upper-${i}-head-guard`, r.x + 1.5, r.y + 1.5, r.x + r.w - 1.5, r.y + 1.5)
      guard(`upper-${i}-foot-return-left`, r.x + 1.5, r.y + r.h - 1.5, r.x + 8.5, r.y + r.h - 1.5)
      guard(
        `upper-${i}-foot-return-right`,
        r.x + 35.5,
        r.y + r.h - 1.5,
        r.x + r.w - 1.5,
        r.y + r.h - 1.5
      )
      // Inlet's four continuous corner posts support each separate stack.
      for (const x of [r.x, r.x + r.w - 3])
        for (const z of [r.y, r.y + r.h - 3]) box(`upper-${i}-floor-post`, x, 0, z, 3, deck, 3)
    }
  })
  g.lowers.forEach((r, i) => {
    const inset = style === 'inlet' ? BUNK_BOARD_THICKNESS : 0
    box(
      `lower-${i}-base`,
      r.x + inset,
      0,
      r.y + inset,
      r.w - 2 * inset,
      CUSTOM.lowerDeck,
      r.h - 2 * inset
    )
    // Keep the support deck above the drawer cavity; raise the surrounding
    // frame to capture the bottom 2 inches of the actual-size mattress.
    const recess = 2, clearance = 0.5
    const alongX = r.axis === 'x'
    const mattressWidth = r.size.startsWith('Twin') ? 38 : r.size === 'Queen' ? 60 : 54
    const mattressLength = r.size === 'Twin XL' || r.size === 'Queen' ? 80 : 75
    const sideX = (r.w - (alongX ? mattressLength : mattressWidth)) / 2 - clearance
    const sideZ = (r.h - (alongX ? mattressWidth : mattressLength)) / 2 - clearance
    const y = CUSTOM.lowerDeck
    // Existing headboards close the head end. The three raised rails meet
    // without overlapping corners and leave half an inch around the bedding.
    if (r.head !== 'left')
      box(`lower-${i}-recess-left`, r.x + inset, y, r.y + inset, sideX - inset, recess, r.h - 2 * inset)
    if (r.head !== 'right')
      box(`lower-${i}-recess-right`, r.x + r.w - sideX, y, r.y + inset, sideX - inset, recess, r.h - 2 * inset)
    if (r.head !== 'back')
      box(`lower-${i}-recess-back`, r.x + sideX, y, r.y + inset, r.w - 2 * sideX, recess, sideZ - inset)
    box(`lower-${i}-recess-foot`, r.x + sideX, y, r.y + r.h - sideZ, r.w - 2 * sideX, recess, sideZ - inset)
    addBedTrim(root,r,CUSTOM.lowerDeck,true,bedTrim,bed,customTrimOmissions(bed,r,false))
    bedding(r, CUSTOM.lowerDeck, `lower-${i}`)
    if (r.head === 'back')
      box(`lower-${i}-headboard`, r.x + inset, 12, r.y + inset, r.w - 2 * inset, 18, 2)
    else box(`lower-${i}-headboard`, r.x, 12, r.y, 2, 18, r.h)
    if (style !== 'inlet')
      for (let n = 0; n < 2; n++) {
        const w = r.w / 2 - 2,
          x = r.x + 1 + (n * r.w) / 2,
          z = r.y + r.h - (style === 'rosemary' ? 0 : 0.5)
        const face = createDrawerFace(w, 10, drawerFaceStyle, drawerPullStyle)
        face.position.copy(local(x, 1, z));root.add(face)
      }
  })
  if (style === 'watercolor') {
    box('paneled-head-enclosure', 0, 0, 0, 3, deck, 44)
    for (let z = 6; z < 44; z += 6)
      box('enclosure-panel-joint', 3, 0, z, 0.12, deck, 0.15, 'accent')
  }
  if (g.landing && g.stairs) {
    const { landing: l, stairs: s } = g,
      stairProfile = customStairProfile(bed),
      deck = stairProfile.landingHeight,
      side = CUSTOM.side
    box('shared-landing', l.x, deck - 2, l.y, l.w, 2, l.h, 'accent')
    const stringer = bed.stairStyle === 'stringer', openRisers = hasOpenRiserSides(bed)
    if (stringer) {
      for (const [i, post] of customStringerSupportPosts(bed).entries())
        box(`stringer-support-post-${i}`, post.x, 0, post.y, post.w, hasCompactStringerStairs(bed) ? profile.deckHeight - frameDepth : deck - 10, post.h)
      // The paired layout's slim posts sit flush beneath both front inner bunk corners.
      // Retain the landing header and rear wall ledger in this planning preview.
      box('stringer-landing-header', l.x, deck - 10, l.y + l.h - 3.5, l.w, 8, 3.5)
    } else if (openRisers) {
      for (const x of [s.x, s.x + s.w - side])
        box('open-riser-landing-post', x, 0, s.y - side, side, deck - 2, side)
      box('open-riser-landing-header', l.x, deck - 10, s.y - side, l.w, 8, side)
    } else {
      for (const x of [l.x, l.x + l.w - side])
        box('landing-carcass-side', x, 0, l.y, side, deck - 2, l.h)
    }
    if (style === 'scenic') box('landing-wall-ledger', l.x + l.w - 3, deck - 8, l.y, 3, 6, l.h)
    else box('landing-wall-ledger', l.x, deck - 8, l.y, l.w, 6, 3)
    if (style === 'watercolor' || style === 'scenic')
      guard('landing-outer-guard', l.x + l.w - 1.5, l.y + 1.5, l.x + l.w - 1.5, l.y + l.h - 1.5)
    // Paired bunks retain their connecting rear rails even against a solid wall.
    // Other layouts retain rear guarding at windows and exposed edges.
    const railFace = guardBars === 3 ? 2.5 : 3.5
    const railBase = pattern === 'cross' ? deck : deck + CUSTOM.mattress + 3.5
    const railTop =
      pattern === 'horizontal'
        ? railBase + guardBars * railFace + (guardBars - 1) * 3.5
        : isXGuardrail(guardrailStyle)
          ? railBase + X_GUARDRAIL.height
          : pattern === 'cross'
            ? deck + 21
            : deck + 30
    if (style === 'bridge' || style === 'rosemary' || landingRearNeedsGuard(bed, room, openings, railTop)) {
      if (bed.windowGuardSolid && hasCompactStairs(bed)) {
        const top = profile.deckHeight + frameRise, height = top - deck
        const stock = 11/16, z = l.y + (side-stock)/2
        if (height > 0) {
          box('landing-window-solid-panel',l.x,deck,z,l.w,height,stock)
          const face = createFacePanel([new T.Vector2(0,0),new T.Vector2(l.w,0),new T.Vector2(l.w,height),new T.Vector2(0,height)],upperBunkFaceStyle === 'flat' ? 'nickel_gap' : upperBunkFaceStyle)
          face.name = 'landing-window-matching-face'
          face.position.copy(local(l.x,deck,z+stock));root.add(face)
        }
      } else if (pattern === 'cross') {
        // Seat the complete rear panel on the landing, level with the bunk panels.
        const panel = createXGuardrailPanel(l.w, 'landing-back-guard-x', railTop - railBase)
        panel.position.copy(local(l.x, railBase, l.y + (side - X_GUARDRAIL.stock) / 2))
        root.add(panel)
      } else if (pattern === 'horizontal') {
        for (const x of [l.x, l.x + l.w - side])
          box('landing-back-guard-post', x, deck, l.y, side, railTop - deck, side)
        for (let n = 0; n < guardBars; n++)
          box(
            'landing-back-guard-rail',
            l.x + side,
            railBase + n * (railFace + 3.5),
            l.y + (side - BUNK_BOARD_THICKNESS) / 2,
            l.w - 2 * side,
            railFace,
            BUNK_BOARD_THICKNESS,
            n === guardBars - 1 ? 'topRail' : n === 0 ? 'rail1' : 'rail2'
          )
      } else {
        guard('landing-back-guard', l.x + 1.5, l.y + 1.5, l.x + l.w - 1.5, l.y + 1.5, pattern, deck)
      }
    }
    const stepInset = bed.stairStyle && bed.stairStyle !== 'enclosed' ? 0 : side
    const count = stairProfile.treads,
      run = s.h / count,
      rise = stairProfile.rise
    for (let i = 0; i < count; i++) {
      const z = s.y + i * run,
        h = deck - (i + 1) * rise
      if (!stringer && !openRisers) box(
        `stair-riser-${i}`,
        s.x + stepInset,
        0,
        z,
        s.w - 2 * stepInset,
        h - 1,
        style === 'scenic' ? run - 0.4 : run
      )
      box(`stair-tread-${i}`, s.x + stepInset, h - 1, z, s.w - 2 * stepInset, 1, run, 'accent')
      if (style === 'scenic' && !stringer) {
        const dy = h - rise + 0.5,
          dh = rise - 2,
          x = s.x + stepInset + 1,
          w = s.w - 2 * stepInset - 2,
          slotY = h - 3,
          slotX = s.x + s.w / 2 - 3
        // A recessed finger pull, with a real opening in the drawer face. No coplanar overlays.
        box(`stair-drawer-${i}-below`, x, dy, z + run - 0.3, w, slotY - dy, 0.3)
        box(`stair-drawer-${i}-above`, x, slotY + 0.5, z + run - 0.3, w, dy + dh - slotY - 0.5, 0.3)
        box(`stair-drawer-${i}-left`, x, slotY, z + run - 0.3, slotX - x, 0.5, 0.3)
        box(`stair-drawer-${i}-right`, slotX + 6, slotY, z + run - 0.3, x + w - slotX - 6, 0.5, 0.3)
        box(`stair-drawer-pull-${i}`, slotX, slotY, z + run - 0.25, 6, 0.5, 0.05, 'accent')
      }
    }
    if (stringer) {
      for (const [i, x] of [s.x + 1, s.x + s.w - 2.5].entries()) {
        const support = add(`exposed-stringer-${i}`, stringerGeometry(count, run, rise, deck - rise))
        support.position.copy(local(x, 0, s.y))
        const face = createFacePanel(stringerOutline(count, run, rise, deck - rise), stringerFaceStyle)
        face.name = `stringer-${i}-face-${stringerFaceStyle}`
        face.rotation.y = Math.PI / 2
        if(i === 0) face.scale.z = -1
        face.position.copy(local(i === 0 ? x : x + 1.5, 0, s.y))
        root.add(face)
      }
    }
    // With X rails the solid cheek/cap ends at the underside of the inside
    // return. The existing X return remains exposed above the staircase.
    const stairSideTop = openRisers && pattern === 'cross' ? railBase : railTop
    // Plumb-cut cap and cheek share one slope. No rotated-block overhang or
    // white triangular fin at either end. A flat-capped newel meets the landing end.
    const capThickness = 1.5,
      top = deck + 30,
      bottom = rise + 30
    function slopedSection(
      name: string,
      x: number,
      lowTop: number,
      lowBottom: number,
      highTop: number,
      highBottom: number,
      part: FinishPart
    ) {
      const shape = new T.Shape()
      const flatCap = openRisers && part === 'accent' && lowTop > railTop - capThickness
      shape.moveTo(0, flatCap ? railTop - capThickness : lowTop)
      if (flatCap) shape.lineTo(-s.h * (top - railTop) / (top - bottom), railTop - capThickness)
      shape.lineTo(-s.h, lowBottom)
      shape.lineTo(-s.h, highBottom)
      if (openRisers && highTop > railTop - (part === 'frame' ? capThickness : 0)) {
        const limit = railTop - (part === 'frame' ? capThickness : 0)
        const along = s.h * (highTop - limit) / (highTop - highBottom)
        shape.lineTo(-along, limit)
        shape.lineTo(0, limit)
      } else shape.lineTo(0, highTop)
      shape.closePath()
      if (openRisers) {
        const lower = g.lowers[x === s.x ? 0 : 1]
        const tail = hasStairDimensions(bed)
          ? 0 : Math.max(0.5, lower.y + lower.h - (s.y + s.h))
        const profile = facetedStairProfile(s.h, stairSideTop, Math.min(CUSTOM.lowerDeck + 2, Math.max(0, stairSideTop - capThickness * 2)), tail, capThickness)
        const points = part === 'frame' ? profile.cheek : profile.cap
        shape.curves = []
        shape.moveTo(points[0].x, points[0].y)
        for (const point of points.slice(1)) shape.lineTo(point.x, point.y)
        shape.closePath()
      }
      const geometry = new T.ExtrudeGeometry(shape, { depth: side, bevelEnabled: false, steps: 1 })
      geometry.rotateY(Math.PI / 2)
      geometry.scale(1 / 12, 1 / 12, 1 / 12)
      const mesh = add(name, geometry, part)
      mesh.position.copy(local(x, 0, s.y))
      if (name === 'stair-solid-side') {
        // Use the cheek's exact clipped profile so courses stop below the cap.
        const outline = shape.getPoints()
        if (outline[0].equals(outline[outline.length - 1])) outline.pop()
        const face = createFacePanel(outline, stringerFaceStyle)
        const left = x === s.x
        face.name = `stair-side-${left ? 'left' : 'right'}-face-${stringerFaceStyle}`
        face.rotation.y = Math.PI / 2
        if (left) face.scale.z = -1
        face.position.copy(local(left ? x - 0.02 : x + side + 0.02, 0, s.y))
        root.add(face)
        // Both sides of the stair enclosure carry the same clipped courses.
        const inside = createFacePanel(outline, stringerFaceStyle)
        inside.name = `stair-side-${left ? 'left' : 'right'}-inside-face-${stringerFaceStyle}`
        inside.rotation.y = Math.PI / 2
        if (!left) inside.scale.z = -1
        inside.position.copy(local(left ? x + side + 0.02 : x - 0.02, 0, s.y))
        root.add(inside)
      }
      return mesh
    }
    for (const x of bed.stairStyle && bed.stairStyle !== 'enclosed' ? [] : [s.x, s.x + s.w - side]) {
      const cheek = slopedSection(
        'stair-solid-side',
        x,
        0,
        0,
        top - capThickness,
        bottom - capThickness,
        'frame'
      )
      cheek.userData.stairSide = { x, width: side, z: s.y, run: s.h }
      if (openRisers && style !== 'rosemary') {
        const lower = g.lowers[x === s.x ? 0 : 1]
        const innerX = x === s.x ? lower.x + lower.w : lower.x
        const edgeX = x === s.x ? x : x + side
        const gap = Math.abs(innerX - edgeX)
        if (gap > 0 && (x === s.x ? innerX <= edgeX : innerX >= edgeX))
          box('stair-to-lower-frame-return', Math.min(innerX, edgeX), 0,
            lower.y + lower.h - side, gap, CUSTOM.lowerDeck + 2, side)
      }
      slopedSection(
        'stair-continuous-cap',
        x,
        top - capThickness,
        bottom - capThickness,
        top,
        bottom,
        'accent'
      )
      const newelTop = openRisers ? stairSideTop : top
      if (newelTop > deck + capThickness) box('stair-landing-newel', x, deck, s.y - side, side, newelTop - capThickness - deck, side)
      box(
        'stair-landing-newel-cap',
        x,
        newelTop - capThickness,
        s.y - side,
        side,
        capThickness,
        side,
        'accent'
      )
    }
  }
  g.ladders.forEach((l, i) => {
    const face = guardBars === 3 ? 2.5 : 3.5
    const railTop =
      pattern === 'cross'
        ? deck + X_GUARDRAIL.height
        : deck + CUSTOM.mattress + 3.5 + guardBars * face + (guardBars - 1) * 3.5
    const dividerLadder = style === 'divider'
    const railXs = dividerLadder
      ? i === 0 ? [l.x, l.x + l.w - 2] : [l.x, l.x + l.w - 2]
      : [l.x - 2, l.x + l.w]
    for (const x of railXs) box(`ladder-${i}-rail`, x, 0, l.y, 2, railTop, 4)
    for (let y = 9; y < deck; y += 9) box(`ladder-${i}-rung`, l.x + (dividerLadder ? 2 : 0), y, l.y, l.w - (dividerLadder ? 4 : 0), 1.5, 4, 'accent')
  })
  if (bed.lowerShiplap) addLowerShiplap(root, bed)
  if (bed.builtInBookshelf) addCabinBookshelf(root, bed)
  if (crownMolding === 'crown_7') addCabinCrown(root, bed, cabinTop)
  const q = bounds(bed)
  root.position.set((q.x + q.w / 2 - room.width / 2) / 12, 0, (q.y + q.h / 2 - room.depth / 2) / 12)
  root.rotation.y = (bed.rotation * Math.PI) / 180
  root.name = `${bed.name} · Bliss Custom planning concept`
  return root
}







